/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { makeTestId } from "app/client/lib/domUtils";
import { urlState } from "app/client/models/gristUrlState";
import { isNarrowScreenObs, theme } from "app/client/ui2018/cssVars";
import { IconName } from "app/client/ui2018/IconList";
import { icon as cssIcon } from "app/client/ui2018/icons";
import { IGristUrlState } from "app/common/gristUrls";
import { useBindable } from "app/common/gutil";

import { BindableValue, dom, IDomArgs, MaybeObsArray, styled, UseCBOwner } from "grainjs";

const testId = makeTestId("test-component-tabs-");

export interface TabProps {
  /** Label to show */
  label: string;
  /* Optional id to use for selected comparison. If not provided label will be compared */
  id?: string;
  /** Icon to show */
  icon?: IconName;
  /** Function to call when tab is clicked */
  onClick?: () => void;
  /** Grist Link state to switch to when tab is clicked. */
  link?: IGristUrlState;
}

export function buildTabs(
  tabs: MaybeObsArray<TabProps>,
  selected: BindableValue<string | null | undefined>,
  ...args: IDomArgs<HTMLDivElement>
) {
  const isSelected = (tab: TabProps) => (use: UseCBOwner) => useBindable(use, selected) === (tab.id ?? tab.label);
  return cssTabs(
    dom.forEach(tabs, tab => cssTab(
      cssIconAndLabel(!tab.icon ? null : cssTabIcon(tab.icon, dom.hide(isNarrowScreenObs())),

        // The combination with space makes the label as wide as its bold version,
        // to avoid slight shifts of other labels when switching tabs.
        dom("div", tab.label, cssBoldLabelSpacer(tab.label))),

      cssTab.cls("-selected", isSelected(tab)),

      tab.onClick && dom.on("click", tab.onClick.bind(tab)),

      tab.link && urlState().setLinkUrl(tab.link, { replace: true }),

      testId("tab"),
      testId("tab-selected", isSelected(tab)),
    )),
    testId("list"),
    ...args,
  );
}

export const cssTabs = styled("div", `
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  border-radius: 12px;
  background: color-mix(in srgb, ${theme.hover} 35%, transparent);
  border: 1px solid color-mix(in srgb, ${theme.controlBorder} 50%, transparent);
  user-select: none;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  width: fit-content;
`);

export const cssTab = styled("a", `
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 14px;
  border-radius: 9px;
  color: ${theme.lightText};
  --icon-color: ${theme.lightText};
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.01em;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.15s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover, &:focus {
    color: ${theme.text};
    --icon-color: ${theme.text};
    text-decoration: none;
  }

  &-selected {
    background: ${theme.mainPanelBg};
    color: ${theme.text};
    --icon-color: ${theme.controlPrimaryBg};
    font-weight: 600;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    border: none;
    margin-bottom: 0;
  }
`);

const cssIconAndLabel = styled("div", `
  display: flex;
  align-items: center;
  column-gap: 8px;
`);

const cssBoldLabelSpacer = styled("div", `
  font-weight: bold;
  height: 1px;
  color: transparent;
  overflow: hidden;
  visibility: hidden;
`);

const cssTabIcon = styled(cssIcon, `
  width: 20px;
  height: 20px;
`);
